import { useCallback, useMemo } from "react";
import {
  parseAsNativeArrayOf,
  parseAsString,
  parseAsStringLiteral,
  type inferParserType,
  useQueryStates,
} from "nuqs";

import {
  normaliseWorkflowHistoryUrlState,
  type WorkflowHistoryQueryValues,
  type WorkflowHistoryUrlState,
  WORKFLOW_HISTORY_PERIOD_VALUES,
} from "./workflow-history-url-state.ts";

export const workflowQueryParsers = {
  workflow: parseAsNativeArrayOf(parseAsString).withDefault([]),
  status: parseAsNativeArrayOf(parseAsString).withDefault([]),
  period: parseAsStringLiteral(WORKFLOW_HISTORY_PERIOD_VALUES).withDefault("24h"),
  from: parseAsString,
  to: parseAsString,
};

export type WorkflowQueryState = inferParserType<typeof workflowQueryParsers>;

export function useWorkflowQueryState(
  options: { history?: "push" | "replace" } = {},
) {
  const [query, setQuery] = useQueryStates(workflowQueryParsers, {
    history: options.history ?? "push",
  });
  const filters = useMemo(
    () => normaliseWorkflowHistoryUrlState(query as WorkflowHistoryQueryValues),
    [query],
  );
  const setFilters = useCallback((update: Partial<WorkflowHistoryUrlState>) => {
    return setQuery({
      ...(update.workflow === undefined ? {} : { workflow: update.workflow }),
      ...(update.status === undefined ? {} : { status: update.status }),
      ...(update.period === undefined ? {} : { period: update.period }),
      ...(update.from === undefined ? {} : { from: update.from || null }),
      ...(update.to === undefined ? {} : { to: update.to || null }),
    });
  }, [setQuery]);

  return { query, filters, setFilters };
}
